import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Dialog as DialogPrimitive } from '@base-ui/react/dialog';
import { Bell, BellOff, CheckCheck, Trash2, X } from 'lucide-react';
import {
  useClearAllNotifications,
  useDeleteNotification,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  useUnreadNotificationsCount,
} from '@/lib/notifications';
import { textFor, linkFor, formatWhen } from '@/lib/notification-display';
import { cn } from '@/lib/cn';
import type { NotificationData } from '@/types/api';

/** Topbar bell with unread badge; opens a right-side flyout of notifications. */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'all' | 'unread'>('all');
  const [confirmingClear, setConfirmingClear] = useState(false);
  const { data: count = 0 } = useUnreadNotificationsCount();
  const { data: items = [], refetch } = useNotifications();
  const markAll = useMarkAllNotificationsRead();
  const clearAll = useClearAllNotifications();

  useEffect(() => {
    if (open) refetch();
    else setConfirmingClear(false);
  }, [open, refetch]);

  const hasUnread = count > 0;
  const visible = tab === 'unread' ? items.filter((n) => !n.read_at) : items;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      <DialogPrimitive.Trigger
        aria-label={hasUnread ? `${count} unread notifications` : 'Notifications'}
        className={cn(
          'relative inline-flex size-9 items-center justify-center rounded-md text-[var(--color-sidebar-muted-foreground)] transition-colors hover:bg-[var(--color-sidebar-accent)] hover:text-[var(--color-sidebar-foreground)]',
          open && 'bg-[var(--color-sidebar-accent)] text-[var(--color-sidebar-foreground)]',
        )}
      >
        <Bell className="size-4" />
        {hasUnread ? (
          <span
            className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-none text-primary-foreground ring-2 ring-[var(--color-sidebar)]"
            aria-hidden
          >
            {count > 99 ? '99+' : count}
          </span>
        ) : null}
      </DialogPrimitive.Trigger>

      <DialogPrimitive.Portal>
        <DialogPrimitive.Backdrop className="fixed inset-0 z-40 bg-black/20 duration-150 data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0" />
        <DialogPrimitive.Popup
          className={cn(
            'fixed inset-y-0 right-0 z-50 flex h-full w-full max-w-sm flex-col bg-card text-card-foreground shadow-2xl outline-none',
            'duration-200 data-open:animate-in data-open:slide-in-from-right data-closed:animate-out data-closed:slide-out-to-right',
          )}
        >
          <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
            <div className="flex items-center gap-2">
              <DialogPrimitive.Title className="text-sm font-semibold">
                Notifications
              </DialogPrimitive.Title>
              {hasUnread ? (
                <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[11px] font-medium text-primary">
                  {count} new
                </span>
              ) : null}
            </div>
            <div className="flex items-center gap-1">
              {hasUnread ? (
                <button
                  type="button"
                  onClick={() => markAll.mutate()}
                  className="inline-flex items-center gap-1 rounded px-1.5 py-1 text-[11px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  <CheckCheck className="size-3" /> Mark all read
                </button>
              ) : null}
              <DialogPrimitive.Close
                aria-label="Close"
                className="inline-flex size-7 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <X className="size-4" />
              </DialogPrimitive.Close>
            </div>
          </div>

          <div className="flex items-center gap-1 border-b border-border px-3 py-2">
            {(['all', 'unread'] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                className={cn(
                  'rounded px-2.5 py-1 text-xs font-medium capitalize transition-colors',
                  tab === t
                    ? 'bg-muted text-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {t}
                {t === 'unread' && hasUnread ? ` (${count})` : ''}
              </button>
            ))}
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {visible.length === 0 ? (
              <div className="px-6 py-16 text-center">
                <BellOff className="mx-auto size-6 text-muted-foreground" />
                <p className="mt-3 text-sm text-muted-foreground">
                  {tab === 'unread' ? 'No unread notifications.' : "You're all caught up."}
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-border/60">
                {visible.map((n) => (
                  <FlyoutRow key={n.id} notification={n} onNavigate={() => setOpen(false)} />
                ))}
              </ul>
            )}
          </div>

          <div className="flex items-center justify-between gap-2 border-t border-border px-4 py-2.5">
            {items.length > 0 ? (
              confirmingClear ? (
                <span className="flex items-center gap-2 text-xs">
                  <span className="text-muted-foreground">Clear all {items.length}?</span>
                  <button
                    type="button"
                    onClick={() => {
                      clearAll.mutate();
                      setConfirmingClear(false);
                    }}
                    className="font-medium text-destructive hover:underline"
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmingClear(false)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    Cancel
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmingClear(true)}
                  className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="size-3" /> Clear all
                </button>
              )
            ) : (
              <span />
            )}
            <Link
              to="/notifications"
              onClick={() => setOpen(false)}
              className="text-xs font-medium text-primary hover:underline"
            >
              View all
            </Link>
          </div>
        </DialogPrimitive.Popup>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function FlyoutRow({
  notification,
  onNavigate,
}: {
  notification: NotificationData;
  onNavigate: () => void;
}) {
  const markRead = useMarkNotificationRead();
  const remove = useDeleteNotification();
  const navigate = useNavigate();
  const { title, body } = textFor(notification);
  const link = linkFor(notification);
  const unread = !notification.read_at;

  function onClick() {
    if (unread) markRead.mutate(notification.id);
    if (link) {
      onNavigate();
      navigate(link);
    }
  }

  return (
    <li
      onClick={onClick}
      className={cn(
        'group/n flex cursor-pointer items-start gap-3 px-4 py-3 transition-colors hover:bg-muted/40',
        unread && 'bg-primary/[0.03]',
      )}
    >
      <span
        className={cn(
          'mt-1.5 size-2 shrink-0 rounded-full',
          unread ? 'bg-primary' : 'bg-transparent ring-1 ring-border',
        )}
        aria-hidden
      />
      <div className="min-w-0 flex-1">
        <span className={cn('block truncate text-sm', unread ? 'font-semibold' : 'font-medium')}>
          {title}
        </span>
        {body ? (
          <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{body}</p>
        ) : null}
        <p className="mt-1 text-[11px] text-muted-foreground/80">
          {formatWhen(notification.created_at)}
        </p>
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          remove.mutate(notification.id);
        }}
        aria-label="Delete"
        className="shrink-0 rounded p-1.5 text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-destructive group-hover/n:opacity-100"
      >
        <Trash2 className="size-3.5" />
      </button>
    </li>
  );
}
